/**
 * Measurement interface for use by virtualized components. Describes pertinent
 * measurements of a virtualized node.
 */
export interface VirtualizedMeasurement {
  height: number;
  offsetTop: number;
  scrollTop: number;
  top: number;
}

/**
 * Helper function to measure a set of document nodes that are in a vertically
 * virtualized component. For example, an infinitely scrolling list.
 * @param container The container node that acts as the offset parent for the
 * set of virtualized nodes being measured.
 * @param nodes Array of virtualized document nodes that should be measured.
 */
export const measureVertical = (
  container: HTMLElement,
  nodes: HTMLElement[],
): VirtualizedMeasurement[] => {
  const rect = container.getBoundingClientRect();
  const css = window.getComputedStyle(container);
  return nodes.map(
    (child: HTMLElement): VirtualizedMeasurement => {
      const { top, height } = child.getBoundingClientRect();
      const borderTopWidth = css.borderTopWidth
        ? parseInt(css.borderTopWidth, 10)
        : 0;
      const scrollTop = top - rect.top - borderTopWidth;
      const offsetTop = top - child.offsetTop;
      return {
        height,
        offsetTop,
        scrollTop,
        top,
      };
    },
  );
};

/**
 * Helper function to measure a set of document nodes within a virtualized
 * component.
 * @param container The container document node that acts as the offset parent
 * for the virtualized nodes.
 * @param nodes Array of virtualized document nodes that should be measured.
 */
export const measureVirtualizedNodes = (
  container: HTMLElement,
  nodes: HTMLElement[],
  direction: 'horizontal' | 'vertical',
): VirtualizedMeasurement[] => {
  if (direction === 'vertical') {
    return measureVertical(container, nodes);
  } else {
    throw new Error('Not implemented!');
  }
};
